import React, { useEffect, useState } from "react";
import { apiService } from "../services/api";
import { useTermsScroll } from "../hooks/useTermsScroll";

interface LoginTermsModalProps {
  open: boolean;
  email: string;
  userId: string;
  onAccept: () => void;
  onClose: () => void;
}

export default function LoginTermsModal({
  open,
  onAccept,
  userId,
  email,
  onClose,
}: LoginTermsModalProps) {
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState("");
  const { checked, setChecked, hasScrolledToBottom, scrollRef, handleScroll } = useTermsScroll(open, terms);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await apiService.getLatestTermsAndConditions();
        if (response?.data?.details) {
          setTerms(response.data.details);
        }
      } catch (error) {
        console.error("Error fetching terms:", error);
      }
    };
    if (open) {
      fetchTerms();
    }
  }, [open]);

  if (!open) return null;

  const handleAccept = async () => {
    if (!checked) return;
    setLoading(true);

    try {
      await apiService.acceptLatestTerms({
        user_id: userId,
        email: email,
      });
      setLoading(false);
      setChecked(false);
      onAccept();
    } catch (error) {
      setLoading(false);
      console.error("Failed to accept terms:", error);
    }
  };

  const terms_and_condition = terms || "loading...";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg max-h-[80vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex flex-col flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">
              Terms & Conditions Update
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-gray-500 text-lg"></i>
            </button>
          </div>
          <p className="text-sm text-amber-600 font-medium">
            Please accept our latest terms and conditions to continue.
          </p>
        </div>

        {/* Scrollable body */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-5"
          dangerouslySetInnerHTML={{ __html: terms_and_condition }}
        />

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-3 flex-shrink-0">
          <label className={`flex items-start gap-3 ${hasScrolledToBottom ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                checked={checked}
                disabled={!hasScrolledToBottom}
                onChange={(e) => setChecked(e.target.checked)}
                className={`w-4 h-4 accent-teal-500 ${hasScrolledToBottom ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              />
            </div>
            <span className="text-sm text-gray-700">
              I have read and agree to the latest Terms & Conditions
            </span>
          </label>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!checked || loading}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${checked && !loading
                  ? "bg-teal-500 text-white hover:bg-teal-600 cursor-pointer"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                "Accept & Continue"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
